import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, ChevronRight, Edit2 } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import SortSelect from "../components/SortSelect";
import { sortByOption, DEFAULT_SORT } from "../utils/sort";
import { groupCurrentBills, getBillDisplayStatus } from "../utils/bills";
import { BILL_FREQUENCIES, repeatUnitWord, MONTH_NAMES, scheduleLabel, frequencyOf, nextDueDate, isRepeatingBill } from "../utils/billFrequency";
import { INVESTMENT_TYPES } from "../utils/investmentTypes";
import { BILL_TYPES } from "../utils/expenseTypes";
import { sourceRequired, paidFromFields, paidFromValue } from "../utils/moneySources";
import MoneySourceSelect from "../components/MoneySourceSelect";
import { useMoneySources } from "../components/useMoneySources";

import { API_BASE, apiFetch } from "../api";

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Type and frequency start empty ("Select") and must be picked, so new entries
// don't all land under "Bills". A monthly bill asks only for the day, a yearly one
// for the month and day; the first due date is worked out from those.
const emptyForm = () => ({
  payee: "",
  type: "",
  schedule: "",
  dueDate: "",
  dueDay: String(new Date().getDate()),
  dueMonth: String(new Date().getMonth()),
  amount: "",
  autopay: false,
  description: "",
  repeatUnit: "",
  repeatEvery: "1",
  // Only for type Investments: the kind of investment it is linked to on the server.
  investmentType: INVESTMENT_TYPES[0],
  // "Paid from": "account:<id>", "card:<id>" or "" (see MoneySourceSelect).
  paidFrom: "",
  // The amount changes each time (electricity, a card bill): it's an estimate.
  variableAmount: false,
});

// Marking a bill paid: one whose amount changes each time asks what was actually paid
// (null if cancelled). Returns the body to send.
export const paidBody = (bill) => {
  if (!bill.variableAmount) return { status: "Paid" };
  const answer = window.prompt(`How much did you pay for ${bill.payee}? (estimated ₹${bill.amount.toLocaleString()})`, String(bill.amount));
  if (answer === null) return null;
  const amount = parseFloat(answer);
  return Number.isNaN(amount) || amount < 0 ? null : { status: "Paid", amount };
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const BillsAndPayments = () => {
  const { tokens, fonts } = useTheme();
  const navigate = useNavigate();

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedSearch, setAppliedSearch] = useState({ startDate: "", endDate: "", search: "" });
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const sources = useMoneySources();

  const loadBills = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch(`${API_BASE}/bills`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load bills");
        return res.json();
      })
      .then((data) => setBills(data))
      .catch(() =>
        setError(
          "Couldn't reach the backend. Make sure it's running with `npm run server`."
        )
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const handleSearchClick = () => {
    setAppliedSearch({ startDate, endDate, search });
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setSearch("");
    setAppliedSearch({ startDate: "", endDate: "", search: "" });
  };

  // Only repeating bills live here; one-time payments are expenses, in Transactions.
  const inputStyle = { ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface };
  const currentBills = groupCurrentBills(bills.filter(isRepeatingBill));

  const filteredData = sortByOption(
    currentBills
      .filter((item) => filter === "All" || item.type.toLowerCase() === filter.toLowerCase())
      .filter((item) => !appliedSearch.startDate || new Date(item.dueDate) >= new Date(appliedSearch.startDate))
      .filter((item) => !appliedSearch.endDate || new Date(item.dueDate) <= new Date(appliedSearch.endDate))
      .filter(
        (item) =>
          !appliedSearch.search || item.payee.toLowerCase().includes(appliedSearch.search.toLowerCase())
      ),
    sortBy,
    { dateKey: "dueDate", amountKey: "amount" }
  );

  const handleToggleStatus = (bill) => {
    const nextStatus = bill.status === "Paid" ? "Pending" : "Paid";
    const body = nextStatus === "Paid" ? paidBody(bill) : { status: nextStatus };
    if (!body) return;
    setBills((prev) => prev.map((b) => (b.id === bill.id ? { ...b, ...body } : b)));
    apiFetch(`${API_BASE}/bills/${bill.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      // Paying a repeating bill lines up its next one on the server.
      .then(() => nextStatus === "Paid" && loadBills())
      .catch(() => loadBills());
  };

  const handleDelete = (id) => {
    const previous = bills;
    setBills((prev) => prev.filter((b) => b.id !== id));
    apiFetch(`${API_BASE}/bills/${id}`, { method: "DELETE" }).catch(() => setBills(previous));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError(null);

    const amount = parseFloat(form.amount);
    const repeats = form.repeatUnit && form.repeatUnit !== "once";
    const repeatEvery = repeats ? parseInt(form.repeatEvery, 10) : 1;
    // A new monthly/yearly bill is entered as a day (and month); editing works on one
    // particular occurrence, so it keeps a full date.
    const pickDay = !editingId && (form.repeatUnit === "months" || form.repeatUnit === "years");
    const dueDate = pickDay ? nextDueDate(form.repeatUnit, { day: parseInt(form.dueDay, 10), month: parseInt(form.dueMonth, 10) }) : form.dueDate;
    if (!form.payee.trim()) return setFormError("Payee is required.");
    if (!form.repeatUnit) return setFormError("Choose a frequency.");
    if (repeats && (Number.isNaN(repeatEvery) || repeatEvery < 1 || repeatEvery > 365)) return setFormError("Repeats every must be from 1 to 365.");
    if (!dueDate) return setFormError("Due date is required.");
    if (!form.type) return setFormError("Choose a type.");
    if (Number.isNaN(amount) || amount < 0) return setFormError("Amount must be a valid number.");
    if (!form.paidFrom && sourceRequired(dueDate, sources.assets, sources.cards)) return setFormError("Choose where it's paid from.");

    setSaving(true);
    const { investmentType, dueDay, dueMonth, paidFrom, ...rest } = form;
    const body = { ...rest, ...paidFromFields(paidFrom), variableAmount: !!repeats && form.variableAmount, dueDate, amount, repeatEvery, schedule: scheduleLabel(form.repeatUnit, repeatEvery) };
    // A new one-time entry is an expense: Paid once its date has come.
    if (!editingId && !repeats) body.status = dueDate > todayIso() ? "Pending" : "Paid";
    // Empty when editing means "leave the investment's kind as it is".
    if (form.type === "Investments" && investmentType) body.investmentType = investmentType;

    if (editingId) {
      apiFetch(`${API_BASE}/bills/${editingId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
        .then((res) => {
          if (!res.ok) return res.json().then((data) => Promise.reject(new Error(data.error)));
          return res.json();
        })
        .then((updated) => {
          setBills((prev) => prev.map((b) => (b.id === editingId ? updated : b)));
          closeForm();
        })
        .catch((err) => setFormError(err.message || "Failed to update bill."))
        .finally(() => setSaving(false));
      return;
    }

    apiFetch(`${API_BASE}/bills`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((data) => Promise.reject(new Error(data.error)));
        return res.json();
      })
      .then((created) => {
        setBills((prev) => [...prev, created]);
        closeForm();
        // One-time entries don't show on this page; say where they went.
        if (!isRepeatingBill(created)) {
          setNotice(created.type === "Investments" ? `${created.payee} was added to your investments.` : `${created.payee} was saved as an expense. You'll find it in Transactions.`);
        }
      })
      .catch((err) => setFormError(err.message || "Failed to add bill."))
      .finally(() => setSaving(false));
  };

  const closeForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(false);
    setFormError(null);
  };

  const handleEditClick = (item) => {
    const { repeatUnit, repeatEvery } = frequencyOf(item);
    setForm({
      payee: item.payee,
      type: item.type,
      schedule: item.schedule,
      dueDate: item.dueDate,
      amount: String(item.amount),
      autopay: !!item.autopay,
      description: item.description || "",
      repeatUnit,
      repeatEvery: String(repeatEvery),
      dueDay: "",
      dueMonth: "",
      investmentType: "",
      paidFrom: paidFromValue(item),
      variableAmount: !!item.variableAmount,
    });
    setNotice(null);
    setEditingId(item.id);
    setFormError(null);
    setShowForm(true);
  };

  return (
    <div style={{ ...styles.page, backgroundColor: tokens.bg, fontFamily: fonts.body }}>
      <h2 style={{ ...styles.heading, fontFamily: fonts.display, color: tokens.textPrimary }}>
        Bills &amp; Payments
      </h2>
      <p style={{ ...styles.subheading, color: tokens.textMuted }}>
        Your recurring bills at a glance. Click one to see its details and full payment history.
      </p>

      <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
        {error && (
          <div style={{ ...styles.banner, backgroundColor: tokens.pendingBg, color: tokens.pending }}>
            {error}
          </div>
        )}
        {notice && (
          <div role="status" style={{ ...styles.banner, backgroundColor: tokens.positiveBg, color: tokens.positive }}>
            {notice}
          </div>
        )}

        {/* Button Group */}
        <div style={styles.buttonGroup}>
          {["All", ...BILL_TYPES].map((btn) => (
            <button
              key={btn}
              onClick={() => setFilter(btn)}
              style={{
                ...styles.btn,
                backgroundColor: filter === btn ? tokens.accent : tokens.neutralSoft,
                color: filter === btn ? tokens.accentContrast : tokens.textPrimary,
              }}
            >
              {btn}
            </button>
          ))}
          <button
            onClick={() => {
              if (showForm) closeForm();
              else {
                setForm(emptyForm());
                setEditingId(null);
                setNotice(null);
                setShowForm(true);
              }
            }}
            style={{ ...styles.addBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}
          >
            <Plus size={15} strokeWidth={3} />
            Add Bill
          </button>
          <SortSelect value={sortBy} onChange={setSortBy} />
        </div>

        {showForm && (
          <form onSubmit={handleFormSubmit} style={{ ...styles.form, border: `1px solid ${tokens.surfaceBorder}` }}>
            <span style={{ ...styles.formModeLabel, color: tokens.textMuted }}>
              {editingId ? "Editing bill" : "New bill"}
            </span>
            <input
              placeholder="Payee"
              value={form.payee}
              onChange={(e) => setForm({ ...form, payee: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <select
              aria-label="Frequency"
              value={form.repeatUnit}
              onChange={(e) => setForm({ ...form, repeatUnit: e.target.value })}
              style={{ ...inputStyle, color: form.repeatUnit ? tokens.textPrimary : tokens.textMuted }}
            >
              <option value="" disabled>Frequency: Select</option>
              {BILL_FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            {form.repeatUnit && form.repeatUnit !== "once" && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary }}>
                Every
                <input
                  type="number"
                  min="1"
                  max="365"
                  aria-label="Repeats every"
                  value={form.repeatEvery}
                  onChange={(e) => setForm({ ...form, repeatEvery: e.target.value })}
                  style={{ ...inputStyle, width: 70, minWidth: 0, flex: "0 0 70px" }}
                />
                {repeatUnitWord(form.repeatUnit, form.repeatEvery)}
              </label>
            )}
            {!editingId && form.repeatUnit === "years" && (
              <select aria-label="Due month" value={form.dueMonth} onChange={(e) => setForm({ ...form, dueMonth: e.target.value })} style={inputStyle}>
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
            )}
            {!editingId && (form.repeatUnit === "months" || form.repeatUnit === "years") && (
              <select aria-label="Due day" value={form.dueDay} onChange={(e) => setForm({ ...form, dueDay: e.target.value })} style={inputStyle}>
                {DAYS.map((d) => (
                  <option key={d} value={d}>{form.repeatUnit === "months" ? `Due on day ${d}` : d}</option>
                ))}
              </select>
            )}
            {(editingId || form.repeatUnit === "once" || form.repeatUnit === "days") && (
              <input
                type="date"
                aria-label="Due date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                style={inputStyle}
              />
            )}
            <select
              aria-label="Type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={{ ...inputStyle, color: form.type ? tokens.textPrimary : tokens.textMuted }}
            >
              <option value="" disabled>Type: Select</option>
              {BILL_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {form.type === "Investments" && (
              <select
                aria-label="Investment type"
                value={form.investmentType}
                onChange={(e) => setForm({ ...form, investmentType: e.target.value })}
                style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
              >
                {editingId && <option value="">Investment type: keep as is</option>}
                {INVESTMENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            )}
            {!editingId && form.repeatUnit === "once" && (
              <span style={{ flexBasis: "100%", fontSize: 12.5, color: tokens.textMuted }}>
                {form.type === "Investments"
                  ? "One-time investments go to your investments, not to Bills & Payments."
                  : "One-time payments are saved as an expense (in Transactions), not in Bills & Payments."}
              </span>
            )}
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary }}>
              Paid from
              <MoneySourceSelect
                style={inputStyle}
                value={form.paidFrom}
                onChange={(v) => setForm({ ...form, paidFrom: v })}
                assets={sources.assets}
                cards={sources.cards}
                required={sourceRequired(form.dueDate || todayIso(), sources.assets, sources.cards)}
              />
            </label>
            <input
              placeholder="Description (optional notes)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              style={{ ...styles.formInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface, minWidth: "220px" }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.autopay}
                onChange={(e) => setForm({ ...form, autopay: e.target.checked })}
              />
              Autopay
            </label>
            {form.repeatUnit && form.repeatUnit !== "once" && (
              <label title="Like electricity or a card bill: the amount is an estimate, and you enter the actual one when it's paid" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, color: tokens.textPrimary, cursor: "pointer" }}>
                <input type="checkbox" checked={form.variableAmount} onChange={(e) => setForm({ ...form, variableAmount: e.target.checked })} />
                Amount changes each time
              </label>
            )}
            <button
              type="submit"
              disabled={saving}
              style={{ ...styles.saveBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}
            >
              {saving ? "Saving..." : editingId ? "Save Changes" : "Save"}
            </button>
            <button
              type="button"
              onClick={closeForm}
              style={{ ...styles.saveBtn, backgroundColor: "transparent", color: tokens.textMuted }}
            >
              Cancel
            </button>
            {formError && <span style={{ ...styles.formError, color: tokens.negative }}>{formError}</span>}
          </form>
        )}

        {/* Search aligned right */}
        <div style={styles.dateRangeFilter}>
          <div style={styles.dateField}>
            <label style={{ ...styles.label, color: tokens.textMuted }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ ...styles.dateInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
          </div>

          <div style={styles.dateField}>
            <label style={{ ...styles.label, color: tokens.textMuted }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ ...styles.dateInput, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, backgroundColor: tokens.surface }}
            />
          </div>

          <button onClick={handleSearchClick} style={{ ...styles.searchBtn, backgroundColor: tokens.accent, color: tokens.accentContrast }}>
            Search
          </button>

          <button onClick={handleClear} style={{ ...styles.clearBtn, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>
            Clear
          </button>
        </div>

        {/* Table */}
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Payee</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Type</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Schedule</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Due Date</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Amount Due</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Status</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted }}>Action</th>
              <th style={{ ...styles.th, borderColor: tokens.surfaceBorder, color: tokens.textMuted, width: 32 }} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textMuted, textAlign: "center" }}>
                  Loading bills...
                </td>
              </tr>
            )}
            {!loading && !error && filteredData.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textMuted, textAlign: "center" }}>
                  No bills match these filters.
                </td>
              </tr>
            )}
            {filteredData.map((item) => (
              <tr
                key={item.id}
                onClick={() => navigate(`/bills-and-payments/${item.id}`)}
                style={{ cursor: "pointer" }}
              >
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textPrimary }}>
                  <div>
                    {item.payee}
                    {!!item.autopay && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10.5,
                          fontWeight: 700,
                          padding: "2px 7px",
                          borderRadius: 999,
                          backgroundColor: tokens.accentSoft,
                          color: tokens.accent,
                        }}
                      >
                        AUTOPAY
                      </span>
                    )}
                  </div>
                  {!!item.description && (
                    <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2, fontWeight: 400 }}>
                      {item.description}
                    </div>
                  )}
                </td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textPrimary }}>{item.type}</td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textPrimary }}>{item.schedule}</td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textPrimary }}>{item.dueDate}</td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder, color: tokens.textPrimary, fontFamily: fonts.mono }}>
                  {item.variableAmount && item.status !== "Paid" ? "~" : ""}₹{item.amount.toLocaleString()}
                </td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder }}>
                  {(() => {
                    const displayStatus = getBillDisplayStatus(item);
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleStatus(item); }}
                        style={{
                          ...styles.status,
                          backgroundColor:
                            displayStatus === "Paid" ? tokens.positiveBg
                            : displayStatus === "Pending" || displayStatus === "Due" ? tokens.pendingBg
                            : tokens.neutralSoft,
                          color:
                            displayStatus === "Paid" ? tokens.positive
                            : displayStatus === "Pending" || displayStatus === "Due" ? tokens.pending
                            : tokens.textMuted,
                        }}
                      >
                        {displayStatus}
                      </button>
                    );
                  })()}
                </td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditClick(item); }}
                      style={{ ...styles.deleteBtn, color: tokens.accent }}
                      aria-label={`Edit ${item.payee}`}
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                      style={{ ...styles.deleteBtn, color: tokens.negative }}
                      aria-label={`Delete ${item.payee}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
                <td style={{ ...styles.td, borderColor: tokens.surfaceBorder, padding: "10px 6px" }}>
                  <ChevronRight size={16} color={tokens.textMuted} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  page: {
    padding: "24px",
    minHeight: "100vh",
    transition: "background-color 0.2s ease",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
  },
  subheading: {
    fontSize: "14px",
    margin: "6px 0 20px",
  },
  card: {
    padding: "24px",
    borderRadius: "16px",
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  banner: {
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "13.5px",
    fontWeight: 600,
    marginBottom: "16px",
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  btn: {
    padding: "10px 16px",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    transition: "0.2s",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 16px",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "14px",
    marginLeft: "auto",
  },
  form: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    padding: "16px",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  formInput: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid",
    fontSize: "14px",
    minWidth: "140px",
  },
  saveBtn: {
    padding: "9px 18px",
    border: "none",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
  },
  formError: {
    fontSize: "13px",
    fontWeight: 600,
  },
  formModeLabel: {
    width: "100%",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    border: "1px solid",
    padding: "10px",
    textAlign: "left",
    fontWeight: "700",
    fontSize: "12.5px",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  td: {
    border: "1px solid",
    padding: "10px",
    textAlign: "left",
    fontSize: "14px",
  },
  status: {
    padding: "5px 12px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "600",
    display: "inline-block",
    border: "none",
    cursor: "pointer",
  },
  deleteBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    padding: "6px",
  },
  dateRangeFilter: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  dateField: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "4px",
  },
  dateInput: {
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid",
    fontSize: "14px",
  },
  searchBtn: {
    padding: "9px 16px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontSize: "13.5px",
    fontWeight: "700",
  },
  clearBtn: {
    padding: "9px 16px",
    borderRadius: "8px",
    background: "transparent",
    border: "1px solid",
    cursor: "pointer",
    fontSize: "13.5px",
    fontWeight: "600",
  },
};

export default BillsAndPayments;
